import { useEffect, useRef, useState } from 'react';
import API from '@/lib/api';
import { defaultPrimaryActions } from '@/lib/documentActions';

export const DOCUMENT_ACTION_KEYS = ['pdf', 'download', 'email', 'dup', 'edit', 'cn', 'pay', 'del', 'convert', 'send_status', 'billit', 'chauffeur', 'cmr', 'etiket', 'deellevering', 'verzendmelding', 'handtekening', 'approve'];
const defaults = type => ({ enabled: true, actions: Object.fromEntries(DOCUMENT_ACTION_KEYS.map(k => [k, true])), primary_actions: defaultPrimaryActions(type) });
const cleanActions = raw => Object.fromEntries(DOCUMENT_ACTION_KEYS.filter(k => typeof raw?.[k] === 'boolean').map(k => [k, raw[k]]));
const mergePatch = (first, next) => ({ ...first, ...next, actions: { ...first?.actions, ...next?.actions } });
const applyPatch = (view, patch) => ({ enabled: typeof patch?.enabled === 'boolean' ? patch.enabled : view.enabled, actions: { ...view.actions, ...cleanActions(patch?.actions) }, primary_actions: Array.isArray(patch?.primary_actions) ? patch.primary_actions.slice(0, 5) : view.primary_actions });
const hasPatch = patch => typeof patch?.enabled === 'boolean' || Array.isArray(patch?.primary_actions) || Object.keys(patch?.actions || {}).length > 0;
const readCache = key => { try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; } };
const writeCache = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* Server persistence works without browser storage. */ } };

function legacyFor(context, docType) {
  try {
    const ownerKey = 'billfac:legacy-document-acties-owner';
    const owner = localStorage.getItem(ownerKey);
    if (owner && owner !== context) return null;
    const raw = JSON.parse(localStorage.getItem(`acties_vis_${docType}`) || 'null');
    const actions = cleanActions(raw);
    if (!Object.keys(actions).length) return null;
    localStorage.setItem(ownerKey, context);
    return { enabled: Object.values(actions).some(Boolean), actions };
  } catch { return null; }
}

// Only explicit changes (or a one-time existing preference migration) write to the API.
// Loading another document type never writes the previous type's state.
export const useDocumentActionPreferences = (user, docType) => {
  const context = user?._id ? `${user._id}:${user.verkoper_id || 'owner'}` : '';
  const scope = `${context}:${docType}`;
  const session = useRef(null);
  const [state, setState] = useState({ scope: '', ...defaults(docType), ready: false, status: 'loading', error: '' });

  useEffect(() => {
    if (!context) return;
    const cacheKey = `billfac:document-acties:${scope}`;
    const cached = readCache(cacheKey);
    const s = { scope, view: applyPatch(defaults(docType), cached), pending: cached?.pending || {}, alive: true, ready: false, writing: false };
    session.current = s;
    const endpoint = `/voorkeuren/document-acties/${docType}`;
    const publish = (status, error = '') => {
      if (!s.alive) return;
      writeCache(cacheKey, { ...s.view, pending: mergePatch(s.inFlight, s.pending) });
      setState({ scope, ...s.view, ready: s.ready, status, error });
    };
    const flush = async () => {
      if (!s.alive || !s.ready || s.writing || !hasPatch(s.pending)) return;
      s.writing = true;
      const patch = s.pending;
      s.pending = {};
      s.inFlight = patch;
      s.controller = new AbortController();
      // Keep the sent patch in cache until acknowledged, including during immediate logout.
      setState(previous => previous.scope === scope ? { ...previous, status: 'saving', error: '' } : previous);
      try {
        const { data } = await API.patch(endpoint, { context, ...patch }, { signal: s.controller.signal, timeout: 15000 });
        if (!s.alive) return;
        s.inFlight = null;
        s.view = applyPatch(applyPatch(defaults(docType), data), s.pending);
        publish(hasPatch(s.pending) ? 'saving' : 'saved');
      } catch (e) {
        if (!s.alive) return;
        s.pending = mergePatch(patch, s.pending);
        s.inFlight = null;
        publish('error', 'Niet op je account bewaard. Je keuze blijft op dit apparaat staan.');
        return;
      } finally { s.writing = false; }
      if (s.alive && hasPatch(s.pending)) flush();
    };
    s.commit = patch => {
      if (!s.alive || !s.ready) return;
      s.view = applyPatch(s.view, patch);
      s.pending = mergePatch(s.pending, patch);
      publish('saving');
      flush();
    };
    const load = async () => {
      if (s.writing || !s.alive) return;
      s.loading?.abort();
      s.loading = new AbortController();
      try {
        const { data } = await API.get(endpoint, { noCache: true, signal: s.loading.signal, timeout: 15000 });
        if (!s.alive) return;
        if (!data.configured && !hasPatch(s.pending)) {
          const legacy = legacyFor(context, docType);
          if (legacy) s.pending = legacy;
        }
        s.view = applyPatch(applyPatch(defaults(docType), data), s.pending);
        s.ready = true;
        publish(hasPatch(s.pending) ? 'saving' : 'saved');
        flush();
      } catch (e) {
        if (!s.alive || e.code === 'ERR_CANCELED') return;
        s.ready = true;
        publish('error', 'Accountvoorkeur niet geladen. Tijdelijk wordt de lokale keuze getoond.');
      }
    };
    s.retry = load;
    setState({ scope, ...s.view, ready: false, status: 'loading', error: '' });
    load();
    window.addEventListener('online', load);
    return () => {
      s.alive = false;
      s.loading?.abort();
      s.controller?.abort();
      window.removeEventListener('online', load);
    };
  }, [context, docType, scope]);

  const current = state.scope === scope ? state : { ...defaults(docType), ready: false, status: 'loading', error: '' };
  const commit = patch => { if (session.current?.scope === scope) session.current.commit?.(patch); };
  return { ...current,
    visible: current.enabled ? current.actions : Object.fromEntries(DOCUMENT_ACTION_KEYS.map(k => [k, false])),
    toggleEnabled: () => {
      const view=session.current?.view;
      if(!view)return;
      commit({ enabled:!view.enabled, ...(!view.enabled && !Object.values(view.actions).some(Boolean) ? {actions:defaults().actions} : {}) });
    },
    toggleAction: key => { if (DOCUMENT_ACTION_KEYS.includes(key)) commit({ actions: { [key]: !session.current?.view.actions[key] } }); },
    setPrimary: keys => commit({ primary_actions: keys }),
    resetPrimary: () => commit({ primary_actions: defaultPrimaryActions(docType) }),
    retry: () => session.current?.scope === scope && session.current.retry?.(),
  };
};